import { useEffect, useState } from "react";
import {
  ProductsDataType,
  SpecOption,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import {
  Input,
  message,
  Modal,
  Popconfirm,
  Switch,
  Table,
  TableProps,
} from "antd";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { ProductsService } from "../../services/products.service";
import SelectCountry from "../../components/admin-pages/products/select-country/SelectCountry";
import SelectBrand from "../../components/admin-pages/products/select-brands/SelectBrands";
import SelectCategory from "../../components/admin-pages/products/select-category/SelectCategory";
import SelectOptions from "../../components/admin-pages/products/select-options/SelectOptions";
import { productsSchema } from "./schema/products";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import ProductImages from "../../components/admin-pages/products/product-images/ProductImages";
import ShowImage from "../../components/shared/show-image/ShowImage";
import SelectImage from "../../components/shared/select-image/SelectImage";
import DragImages from "../../components/admin-pages/products/drag-images/DragImages";
import SelectColor from "../../components/admin-pages/products/select-color/SelectColor";
import { FaPlus } from "react-icons/fa";
import SelectWarehouse from "../../components/admin-pages/products/select-warehouse/SelectWarehouse";
import ExportButtons from "./ExportButtons";
import ImportButton from "./ImportButton";

const Products = () => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<ProductsDataType | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [products, setProducts] = useState<ProductsDataType[]>([]);
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [langCode, setLangCode] = useState("AZ");
  const [logoFileList, setLogoFileList] = useState<any[]>([]);
  const [imageList, setImageList] = useState<any[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  );
  const [selectedName, setSelectedName] = useState<string | undefined>(
    undefined
  );
  const [selectedBrandName, setSelectedBrandName] = useState<
    string | undefined
  >(undefined);
  const [selectedColorName, setSelectedColorName] = useState<
    string | undefined
  >(undefined);
  const [selectedWarehouseName, setSelectedWarehouseName] = useState<
    string | undefined
  >(undefined);
  const [imagesModal, setImagesModal] = useState<boolean>(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [key, setKey] = useState(0);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (searchValue.trim() !== "") {
        try {
          const response = await ProductsService.getSearchData(searchValue);
          const searchedData = response?.data?.products?.data?.map(
            (item: ProductsDataType, index: number) => ({
              ...item,
              key: (currentPage - 1) * pageSize + index + 1,
            })
          );
          setProducts(searchedData || []);
          setTotalCount(response?.data?.products?.total || 0);
        } catch (error) {
          console.error("Axtarış zamanı xəta baş verdi", error);
        }
      } else {
        setLoading(true);
        try {
          const response = await ProductsService.getAllProducts(
            currentPage,
            pageSize
          );
          const dataWithKeys = response?.data?.data?.map(
            (item: ProductsDataType, index: number) => ({
              ...item,
              key: (currentPage - 1) * pageSize + index + 1,
            })
          );
          setProducts(dataWithKeys || []);
          setTotalCount(response?.data?.count || 0);
        } catch (error) {
          console.error("Məhsulları gətirərkən xəta baş verdi", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [currentPage, pageSize]);

  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);

  const fetchProducts = async (page: number) => {
    const updatedData = await ProductsService.getAllProducts(page, pageSize);
    setProducts(
      updatedData.data.data.map((item: ProductsDataType, index: number) => ({
        ...item,
        key: (page - 1) * pageSize + index + 1,
      }))
    );
    setTotalCount(updatedData?.data?.count);
  };

  const handleAfterSubmit = async (page: number) => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    await fetchProducts(page);
    setLoading(false);
  };
  const {
    handleSubmit,
    values,
    handleChange,
    setFieldValue,
    resetForm,
    errors,
    touched,
  } = useFormik({
    initialValues: {
      id: "",
      name: "",
      description: "",
      price: null,
      discount_price: null,
      weight: "",
      dimension: "",
      tracking_number: "",
      model_code: "",
      product_group: "",
      category_id: null,
      is_visible: true,
      country_id: null,
      brand_id: null,
      most_sels: false,
      is_new: true,
      translations: [] as {
        lang_code: string;
        name: string;
        description: string;
      }[],
      color_id: null,
      warehouse_id: null,
      specs: [] as SpecOption[],
      cover_image: "",
      images: [] as {
        path: string;
        rank: number;
      }[],
    },
    validationSchema: productsSchema,
    onSubmit: async (values) => {
      const azTranslation = {
        lang_code: "AZ",
        name: values.name,
        description: values.description,
      };

      const updatedTranslations = [
        azTranslation,
        ...values.translations.filter((t) => t.lang_code !== "AZ"),
      ];

      const finalValues = {
        ...values,
        translations: updatedTranslations,
      };

      if (isEditing && editingRecord) {
        if (editingRecord.cover_image === values.cover_image) {
          const payload = {
            name: values.name,
            description: values.description,
            is_visible: values.is_visible,
            price: values.price,
            discount_price: values.discount_price,
            weight: values.weight,
            dimension: values.dimension,
            category_id: values.category_id,
            country_id: values.country_id,
            brand_id: values.brand_id,
            color_id: values.color_id,
            specs: values.specs,
            tracking_number: values.tracking_number,
            model_code: values.model_code,
            most_sels: values.most_sels,
            is_new: values.is_new,
            product_group: values.product_group,
            warehouse_id: values.warehouse_id,
            translations: updatedTranslations,
            images: values.images,
          };
          const response = await ProductsService.updateProduct({
            ...payload,
            id: editingRecord.id,
          });
          if (response.status === 200) {
            message.success("Məhsul uğurla yeniləndi!");
            await handleAfterSubmit(currentPage);
          } else {
            message.error(
              response.errorMessage || "Məhsulu yeniləmək alınmadı."
            );
          }
        } else {
          const response = await ProductsService.updateProduct({
            ...finalValues,
            // cover_image: values.cover_image || '',
            id: editingRecord.id,
          });
          if (response.status === 200) {
            message.success("Məhsul uğurla yeniləndi!");
            await handleAfterSubmit(currentPage);
          } else {
            message.error(
              response.errorMessage || "Məhsulu yeniləmək alınmadı."
            );
          }
        }
      } else {
        const response = await ProductsService.addProduct(finalValues);
        if (response.status === 201) {
          message.success("Məhsul uğurla əlavə edildi!");
          const updatedTotalCount = totalCount + 1;
          // const newPage = Math.ceil(updatedTotalCount / pageSize) || 1;
          setTotalCount(updatedTotalCount);
          // setCurrentPage(newPage);
          setCurrentPage(1);
          // await handleAfterSubmit(newPage);
          await handleAfterSubmit(1);
        } else {
          message.error(
            response.errorMessage || "Məhsul əlavə etmək alınmadı."
          );
        }
      }
      setLoading(false);
    },
  });

  const handleLanguageButtonClick = (lang: string) => {
    setLangCode(lang);
    const existingTranslation = values.translations.find(
      (t) => t.lang_code === lang
    );
    if (!existingTranslation && lang !== "AZ") {
      setFieldValue("translations", [...values.translations]);
    }
  };
  const handleTranslationChange = (value: string, field: string) => {
    if (langCode === "AZ") {
      if (field === "name") {
        setFieldValue("name", value);
      } else if (field === "description") {
        setFieldValue("description", value);
      }
    } else {
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );
      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          [field]: value,
        };
      } else {
        updatedTranslations.push({
          lang_code: langCode,
          name: field === "name" ? value : "",
          description: field === "description" ? value : "",
        });
      }
      setFieldValue("translations", updatedTranslations);
    }
  };

  const handleSwitchChange = async (
    checked: boolean,
    recordId: number,
    fieldName: string
  ) => {
    ProductsService.updateProduct({
      id: recordId,
      [fieldName]: checked,
    });

    if (fieldName === "is_visible") {
      if (checked) {
        message.success("Məhsul artıq saytda görünür.");
      } else {
        message.info("Məhsul artıq saytda görünmür.");
      }
    } else if (fieldName === "most_sels") {
      if (checked) {
        message.success(
          "Məhsul artıq ən çox satılan məhsullar siyahısında görünür."
        );
      } else {
        message.info(
          "Məhsul artıq ən çox satılan məhsullar siyahısında görünmür."
        );
      }
    } else if (fieldName === "is_new") {
      if (checked) {
        message.success("Yeni məhsul");
      } else {
        message.info("Köhnə məhsul");
      }
    }

    setProducts((prevData) =>
      prevData.map((item) =>
        item.id === recordId ? { ...item, [fieldName]: checked } : item
      )
    );
  };

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setLangCode("AZ");
    setFieldValue("cover_image", "");
    setLogoFileList([]);
    setImageList([]);
    setSelectedCategoryId(null);
    setSelectedName(undefined);
    setSelectedBrandName(undefined);
    setSelectedColorName(undefined);
    setSelectedWarehouseName(undefined);
    setFieldValue("specs", []);
    setKey((prevKey) => prevKey + 1);
  };
  const handleEditClick = (record: ProductsDataType) => {

    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("name", record.name);
    setFieldValue("description", record.description);
    setFieldValue("price", record.price);
    setFieldValue("discount_price", record.discount_price);
    setFieldValue("weight", record.weight);
    setFieldValue("dimension", record.dimension);
    setFieldValue("category_id", record.category_id);
    setFieldValue("country_id", record.country_id);
    setFieldValue("brand_id", record.brand_id);
    setFieldValue("color_id", record.color_id);
    setFieldValue("warehouse_id", record.warehouse_id);
    setFieldValue("is_visible", record.is_visible);
    setFieldValue("cover_image", record.cover_image);
    setFieldValue("translations", record.translations);
    setFieldValue("model_code", record.model_code);
    setFieldValue("tracking_number", record.tracking_number);
    setFieldValue("product_group", record.product_group);
    setFieldValue("most_sels", record.most_sels);
    setFieldValue("is_new", record.is_new);
    setFieldValue("specs", record.specs);
    setKey((prevKey) => prevKey + 1);
    setSelectedCategoryId(null);
    setLangCode("AZ");
    setLogoFileList(
      record.cover_image
        ? [
            {
              uid: "-1",
              name: `${record.name} image`,
              status: "done",
              url: record.cover_image,
            },
          ]
        : []
    );

    setImageList(
      record.images.map((img, index) => ({
        uid: `${index}`,
        name: `${record.name} image ${index + 1}`,
        status: "done",
        url: img.path,
      }))
    );
  };

  const handleDeleteClick = async (record: ProductsDataType) => {
    try {
      await ProductsService.deleteProduct(record.id.toString());
      const updatedTotalCount = totalCount - 1;
      const newPage =
        currentPage > 1 && updatedTotalCount <= (currentPage - 1) * pageSize
          ? currentPage - 1
          : currentPage;
      setTotalCount(updatedTotalCount);
      setCurrentPage(newPage);
      await fetchProducts(newPage);
      message.success("Məhsul uğurla silindi!");
    } catch (error) {
      message.error("Məhsulu silmək alınmadı.");
    }
  };

  const columns: TableProps<ProductsDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => (a.key ?? 0) - (b.key ?? 0),
    },
    {
      title: "Adı",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Təsviri",
      dataIndex: "description",
      render: (_, record) => (
        <div style={{ fontSize: "12px" }}>
          {record.description?.length > 15
            ? record.description.substring(0, 15) + "..."
            : record.description}
        </div>
      ),
      sorter: (a, b) => a.description.localeCompare(b.description),
    },
    {
      title: "Qiymət",
      dataIndex: "price",
      sorter: (a, b) => (a.price ?? 0) - (b.price ?? 0),
    },
    {
      title: "Endirim",
      dataIndex: "discount_price",
      render: (discount_price) => (
        <p>{discount_price ? discount_price : "-"}</p>
      ),
      sorter: (a, b) => (a.discount_price ?? 0) - (b.discount_price ?? 0),
    },
    {
      title: "Kateqoriya",
      dataIndex: "category",
      render: (_, record) => (
        <p style={{ fontSize: "12px" }}>{record.category?.name || "-"}</p>
      ),
    },
    {
      title: "Seçimlər",
      dataIndex: "specs",
      render: (_, record) => (
        <div style={{ fontSize: "11px" }}>
          {record.specs?.length > 0
            ? record.specs.map((el, index) => {
                return <div key={index}>{el.text}</div>;
              })
            : "-"}
        </div>
      ),
    },
    {
      title: "Rəng",
      dataIndex: "color_name",
    },
    {
      title: "Model kod",
      dataIndex: "model_code",
      render: (model_code) => (
        <p style={{ fontSize: "12px" }}>{model_code ? model_code : "-"}</p>
      ),
    },
    {
      title: "İzləmə nömrəsi",
      dataIndex: "tracking_number",
      render: (tracking_number) => (
        <p style={{ fontSize: "12px" }}>
          {tracking_number ? tracking_number : "-"}
        </p>
      ),
    },
    {
      title: "Şəkil",
      dataIndex: "cover_image",
      render: (_, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImage(record.cover_image);
            setShowImageModal(true);
          }}
        >
          {record.cover_image ? (
            <img
              style={{ width: "40px" }}
              src={record.cover_image}
              alt={`${record.name.slice(0, 5)}`}
            />
          ) : null}
        </div>
      ),
    },
    {
      title: "Şəkillər",
      dataIndex: "images",
      render: (_, record) => (
        <>
          <div
            style={{ cursor: "pointer" }}
            onClick={() => {
              setSelectedImages(record.images.map((img) => img.path));
              setImagesModal(true);
            }}
          >
            {record.images
              ? record.images
                  .slice(0, 2)
                  .map((image, index) => (
                    <img
                      width={"30px"}
                      key={index}
                      src={image.path}
                      alt={`Şəkil ${index + 1}`}
                    />
                  ))
              : null}
          </div>
        </>
      ),
    },
    {
      title: "Çəki",
      dataIndex: "weight",
      render: (weight) => (
        <p style={{ fontSize: "12px" }}>{weight ? weight : "-"}</p>
      ),
    },
    {
      title: "Brend",
      dataIndex: "brand_name",
      render: (brand_name) => (
        <p style={{ fontSize: "12px" }}>{brand_name ? brand_name : "-"}</p>
      ),
    },
    {
      title: "Ölkə",
      dataIndex: "country",
      render: (country) => (
        <p style={{ fontSize: "12px" }}>
          {country?.name ? country?.name : "-"}
        </p>
      ),
    },
    {
      title: "Anbar",
      dataIndex: "warehouse_name",
      render: (_, record) => (
        <div style={{ fontSize: "12px" }}>
          {record.warehouse_name?.length > 8
            ? record.warehouse_name.substring(0, 8) + "..."
            : record.warehouse_name}
        </div>
      ),
    },
    {
      title: "Ölçü",
      dataIndex: "dimension",
      render: (dimension) => (
        <p style={{ fontSize: "12px" }}>{dimension ? dimension : "-"}</p>
      ),
    },
    {
      title: "Görünürlük",
      dataIndex: "is_visible",
      render: (_, record) => (
        <div className="switch_input">
          <Switch
            checked={record.is_visible}
            onChange={(checked) =>
              handleSwitchChange(checked, record.id, "is_visible")
            }
          />
        </div>
      ),
    },
    {
      title: "Ən çox satılan məhsul",
      dataIndex: "most_sels",
      render: (_, record) => (
        <div className="switch_input">
          <Switch
            checked={record.most_sels}
            onChange={(checked) =>
              handleSwitchChange(checked, record.id, "most_sels")
            }
          />
        </div>
      ),
    },
    {
      title: "Yeni məhsul",
      dataIndex: "is_new",
      render: (_, record) => (
        <div className="switch_input">
          <Switch
            checked={record.is_new}
            onChange={(checked) =>
              handleSwitchChange(checked, record.id, "is_new")
            }
          />
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu səhifəni siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setSearchValue(searchValue);
    setCurrentPage(1);
    if (searchValue.trim() === "") {
      ProductsService.getAllProducts(currentPage, pageSize).then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: ProductsDataType, index: number) => ({
              ...item,
              key: (currentPage - 1) * pageSize + index + 1,
            })
          );
          setProducts(dataWithKeys);
          setTotalCount(response?.data?.count);
        }
      });
    } else {
      ProductsService.getSearchData(searchValue).then((response) => {
        if (Array.isArray(response?.data?.products?.data)) {
          const searchedData = response.data.products.data.map(
            (item: ProductsDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setProducts(searchedData);
          setTotalCount(searchedData.length);
        }
      });
    }
  };

  return (
    <div id="pages">
      <Helmet>
        <title>1412 | Məhsullar</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Məhsullar</h3>
          </div>

          <div className="pages_components">
            <div className="funcional_head">
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  height:"auto"
                }}
              >
                <MainButton onClick={handleAddClick}>
                  <FaPlus size={20} />
                </MainButton>
                <ExportButtons />
                <ImportButton onSuccess={() => fetchProducts(currentPage)} />
              </div>
              <div className="search">
                <Input
                  style={{ borderColor: "#03a9f4" }}
                  placeholder="Məhsulu axtarın..."
                  value={searchValue}
                  onChange={(e) => {
                    e.persist();
                    handleSearch(e);
                  }}
                />
              </div>
              {/* <Select
                style={{ width: "20%", height: "40px" }}
                defaultValue="Mehsullar"
                options={products.map((element) => ({
                  label: element.name,
                  value: element.id,
                }))}
              /> */}
            </div>
            <div className="pages_table">
              <Table<ProductsDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={products}
                rowKey={(record) => record.id.toString()}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalCount,
                  showSizeChanger: true,
                  pageSizeOptions: ["5", "10", "20", "50", "100"],
                  showTotal: (total) => {
                    const displayedProducts = products.length;
                    const displayedText =
                      displayedProducts === pageSize
                        ? `Toplam ${total} məhsul var, Hal-hazırda ${pageSize} göstərilir`
                        : `Toplam ${total} məhsul var, Hal-hazırda ${displayedProducts} göstərilir`;

                    return (
                      <div
                        style={{
                          position: "absolute",
                          left: "10px",
                        }}
                      >
                        {displayedText}
                      </div>
                    );
                  },
                  responsive: true,
                  onChange: (page, pageSize) => {
                    setCurrentPage(page);
                    setPageSize(pageSize);
                  },
                  itemRender: (page, type, originalElement) => {
                    const totalPages = Math.ceil(totalCount / pageSize);

                    if (type === "page") {
                      if (page === 1 || page === 2 || page === totalPages) {
                        return originalElement;
                      }
                      if (page === currentPage) {
                        return originalElement;
                      }
                      if (page === 3 && currentPage > 4) {
                        return <span>...</span>;
                      }
                      if (
                        page === totalPages - 1 &&
                        currentPage < totalPages - 3
                      ) {
                        return <span>...</span>;
                      }
                      if (page >= currentPage - 2 && page <= currentPage + 2) {
                        return originalElement;
                      }
                      return null;
                    }
                    return originalElement;
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}
      <Modal
        title={isEditing ? "Məhsulu redaktə edin" : "Yeni məhsul əlavə edin"}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedCategoryId(null);
          setSelectedName(undefined);
          setSelectedBrandName(undefined);
          setSelectedColorName(undefined);
          setSelectedWarehouseName(undefined);
        }}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
        width={"100%"}
      >
        <div>
          <div className="langs">
            {langs?.map((lang, index) => {
              return (
                <div key={index}>
                  <button
                    className={lang.code === langCode ? "active_lang_btn" : ""}
                    onClick={() => handleLanguageButtonClick(lang.code)}
                  >
                    {lang.name}
                  </button>
                </div>
              );
            })}
          </div>
          <form action="" className="modal_form" onSubmit={handleSubmit}>
            <div className="inputs">
              <div className="form_input products_form_input">
                <div className="input_wrapper">
                  <label className="label" htmlFor="Name">
                    Məhsulun adı {langCode}
                  </label>
                  <input
                    name="name"
                    id="name"
                    placeholder="Məhsulun adı"
                    value={
                      langCode === "AZ"
                        ? values.name
                        : values.translations.find(
                            (t) => t.lang_code === langCode
                          )?.name || ""
                    }
                    onChange={(e) =>
                      handleTranslationChange(e.target.value, "name")
                    }
                  />
                  <span className="focus_border"></span>
                </div>
                <ErrorMessage error={errors.name} touched={touched.name} />
              </div>
              <div className="form_input products_form_input">
                <div className="input_wrapper">
                  <label className="label" htmlFor="description">
                    Məhsulun təsviri {langCode}
                  </label>
                  <input
                    name="description"
                    id="description"
                    placeholder="Məhsulun təsviri"
                    value={
                      langCode === "AZ"
                        ? values.description
                        : values.translations.find(
                            (t) => t.lang_code === langCode
                          )?.description || ""
                    }
                    onChange={(e) =>
                      handleTranslationChange(e.target.value, "description")
                    }
                  />
                  <span className="focus_border"></span>
                </div>
                <ErrorMessage
                  error={errors.description}
                  touched={touched.description}
                />
              </div>
              <div className="form_input products_form_input">
                <div className="input_wrapper">
                  <label className="label" htmlFor="price">
                    Qiymət
                  </label>
                  <input
                    type="number"
                    name="price"
                    id="price"
                    placeholder="Qiymət"
                    value={values.price ?? ""}
                    onChange={handleChange}
                  />
                  <span className="focus_border"></span>
                </div>
                <ErrorMessage error={errors.price} touched={touched.price} />
              </div>
              <div className="form_input products_form_input">
                <div className="input_wrapper">
                  <label className="label" htmlFor="discount_price">
                    Endrimli qiymət
                  </label>
                  <input
                    type="number"
                    name="discount_price"
                    id="discount_price"
                    placeholder="Endrimli qiymət"
                    value={values.discount_price ?? ""}
                    onChange={handleChange}
                  />
                  <span className="focus_border"></span>
                </div>
              </div>
              <div className="form_input products_form_input">
                <div className="input_wrapper">
                  <label className="label" htmlFor="weight">
                    Məhsulun çəkisi
                  </label>
                  <input
                    type="text"
                    name="weight"
                    id="weight"
                    placeholder="Məhsulun çəkisi"
                    value={values.weight || ""}
                    onChange={handleChange}
                  />
                  <span className="focus_border"></span>
                </div>
              </div>
              <div className="form_input products_form_input">
                <div className="input_wrapper">
                  <label className="label" htmlFor="dimension">
                    Məhsulun ölçüsü
                  </label>
                  <input
                    type="text"
                    name="dimension"
                    id="dimension"
                    placeholder="Məhsulun ölçüsü"
                    value={values.dimension || ""}
                    onChange={handleChange}
                  />
                  <span className="focus_border"></span>
                </div>
              </div>
              <div className="form_input products_form_input">
                <div className="input_wrapper">
                  <label className="label" htmlFor="tracking_number">
                    İzləmə nömrəsi
                  </label>
                  <input
                    type="text"
                    name="tracking_number"
                    id="tracking_number"
                    placeholder="İzləmə nömrəsi"
                    value={values.tracking_number || ""}
                    onChange={handleChange}
                  />
                  <span className="focus_border"></span>
                </div>
                <ErrorMessage
                  error={errors.tracking_number}
                  touched={touched.tracking_number}
                />
              </div>
              <div className="form_input products_form_input">
                <div className="input_wrapper">
                  <label className="label" htmlFor="tracking_number">
                    Model kodu
                  </label>
                  <input
                    type="text"
                    name="model_code"
                    id="model_code"
                    placeholder="Model kodu"
                    value={values.model_code || ""}
                    onChange={handleChange}
                  />
                  <span className="focus_border"></span>
                </div>
                <ErrorMessage
                  error={errors.model_code}
                  touched={touched.model_code}
                />
              </div>
              <div className="form_input products_form_input">
                <div className="input_wrapper">
                  <label className="label" htmlFor="tracking_number">
                    Məhsulun aid olduğu qrup
                  </label>
                  <input
                    type="text"
                    name="product_group"
                    id="product_group"
                    placeholder="Məhsulun aid olduğu qrup"
                    value={values.product_group || ""}
                    onChange={handleChange}
                  />
                  <span className="focus_border"></span>
                </div>
                <ErrorMessage
                  error={errors.product_group}
                  touched={touched.product_group}
                />
              </div>
              <div className="form_input products_form_input">
                <SelectCountry
                  setFieldValue={setFieldValue}
                  country_id={editingRecord?.country_id ?? null}
                  selectedName={selectedName}
                  setSelectedName={setSelectedName}
                />
              </div>
              <div className="form_input products_form_input">
                <SelectBrand
                  setFieldValue={setFieldValue}
                  brand_id={editingRecord?.brand_id ?? null}
                  selectedName={selectedBrandName}
                  setSelectedName={setSelectedBrandName}
                />
              </div>
              <div className="form_input products_form_input">
                <SelectColor
                  setFieldValue={setFieldValue}
                  color_id={editingRecord?.color_id ?? null}
                  selectedName={selectedColorName}
                  setSelectedName={setSelectedColorName}
                  field={"color_id"}
                />
              </div>
              <div className="form_input products_form_input">
                <SelectWarehouse
                  setFieldValue={setFieldValue}
                  warehouse_id={editingRecord?.warehouse_id ?? null}
                  selectedName={selectedWarehouseName}
                  setSelectedName={setSelectedWarehouseName}
                />
                <ErrorMessage
                  error={errors.warehouse_id}
                  touched={touched.warehouse_id}
                />
              </div>
              <div className="form_input products_form_input">
                <SelectCategory
                  setFieldValue={setFieldValue}
                  categroryName={editingRecord?.category.name}
                  selectedCategoryId={selectedCategoryId}
                  setSelectedCategoryId={setSelectedCategoryId}
                />
                <ErrorMessage
                  error={errors.category_id}
                  touched={touched.category_id}
                />
              </div>
            </div>
            <div className="form_input_">
              <SelectOptions
                visible={key}
                setFieldValue={setFieldValue}
                specs={editingRecord?.specs ? editingRecord?.specs : []}
              />
            </div>
            <div className="form_input">
              <SelectImage
                image_name="Banner şəkli"
                logoFileList={logoFileList}
                setLogoFileList={setLogoFileList}
                field="cover_image"
                setFieldValue={setFieldValue}
              />
              <ErrorMessage
                error={errors.cover_image}
                touched={touched.cover_image}
              />
            </div>
            <DragImages
              imageList={imageList}
              setImageList={setImageList}
              setFieldValue={setFieldValue}
            />
            <div className="form_input">
              <label className="active_label" htmlFor="">
                Məhsulun saytda görünməsi
              </label>
              <Switch
                checked={values.is_visible}
                onChange={(checked) => setFieldValue("is_visible", checked)}
              />
            </div>
            <div className="form_input">
              <label className="active_label" htmlFor="">
                Ən çox satılan məhsul olaraq seçin
              </label>
              <Switch
                checked={values.most_sels}
                onChange={(checked) => setFieldValue("most_sels", checked)}
              />
            </div>
            <div className="form_input">
              <label className="active_label" htmlFor="">
                Yeni məhsul
              </label>
              <Switch
                checked={values.is_new}
                onChange={(checked) => setFieldValue("is_new", checked)}
              />
            </div>
            <MainButton full type="submit">
              Əlavə edin
            </MainButton>
          </form>
        </div>
      </Modal>
      <ProductImages
        imagesModal={imagesModal}
        setImagesModal={setImagesModal}
        images={selectedImages}
      />
      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};
export default Products;
